import { assocPath, flatten, identity } from 'ramda';
import { parse as parseQueryString, stringify } from 'query-string';

export type QueryStringParameters = Record<string, unknown>;

export interface Parser<T> {
  serialize: (input: T) => QueryStringParameters;
  parse: (input: QueryStringParameters) => T;
}

interface Dependencies<T> {
  parser: Parser<T>;
}

export interface QueryStringSerializer<T = Record<string, unknown>> {
  serialize: (params: T) => string;
  deserialize: (queryString: string) => T;
}

const createIdentityParser = <T>() => ({
  serialize: identity as (input: T) => QueryStringParameters,
  parse: identity as (input: QueryStringParameters) => T,
});

export const createQueryStringSerializer = <T>(dependencies: Partial<Dependencies<T>> = {}) => {
  const { parser = createIdentityParser() } = dependencies as Dependencies<T>;

  const serialize = (values: T) => {
    const serializedValues = parser.serialize(values);

    // Filter out keys with null or undefined values
    const filteredValues = Object.entries(serializedValues).reduce<QueryStringParameters>((acc, [key, value]) => {
      if (value !== null && value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});

    const [arrayValues, otherValues] = separateArrayValues(filteredValues);

    return [arrayToQueryString(arrayValues), stringify(otherValues)].filter(Boolean).join('&');
  };

  function deserialize(input: string) {
    const parsedInput = parseQueryString(input);
    const parameters = Object.entries(parsedInput).reduce((acc, [key, value]: [string, unknown]) => {
      const [name, ...unfilteredPath] = key.split(/\[|]\[|]$/);

      const path = unfilteredPath.filter(Boolean).map((n) => parseInt(n, 10));

      return assocPath([name, ...path], value, acc);
    }, {});

    return parser.parse(parameters);
  }

  const separateArrayValues = (values: QueryStringParameters) =>
    Object.entries(values).reduce(
      ([accArrayValues, accOtherValues], [key, value]) => {
        if (Array.isArray(value)) {
          return [{ ...accArrayValues, [key]: value }, accOtherValues];
        }
        return [accArrayValues, { ...accOtherValues, [key]: value }];
      },
      [{}, {}]
    );

  const arrayToQueryString = (obj: QueryStringParameters) => {
    return flatten(
      Object.entries(obj).map(([key, value]) => {
        const serializedValues = serializeArrayValues(value as string[][]);
        return serializedValues.map((v) => `${key}${v}`);
      })
    ).join('&');
  };

  function serializeArrayValues(arr: string[][]): string[] {
    const result: string[] = [];
    for (let i = 0; i < arr.length; i++) {
      if (!Array.isArray(arr[i])) {
        result.push(`[${i}]=${String(arr[i])}`);
        continue;
      }
      for (let j = 0; j < arr[i].length; j++) {
        result.push(`[${i}][${j}]=${String(arr[i][j])}`);
      }
    }
    return result;
  }

  return {
    serialize,
    deserialize,
  };
};
